"use client";

import React, { createContext, useCallback, useEffect, useState } from "react";
import { isUndefined } from "@/utils/type";
import { mixpanelEvents } from "@/types/mixpanel";
import { DURATION_5_SECS_MILLISECONDS } from "@/constants/time";
import { MIXPANEL_PROJECT_TOKEN } from "@/constants/envs";

export const loadMixpanel = async () => {
  try {
    return (await import("mixpanel-browser")).default;
  } catch (error) {}
};

interface MixpanelContext {
  // eslint-disable-next-line no-unused-vars
  handlePageView: (page: string, options?: any) => void;
  mixpanelInstance: any;
}

const Context = createContext<MixpanelContext | undefined>(undefined);

function MixpanelProvider(props: React.PropsWithChildren<any>) {
  const [mixpanelInstance, setMixpanelInstance] = useState<any>(null);
  const token = MIXPANEL_PROJECT_TOKEN || "";

  useEffect(() => {
    // used this approach to lazy load mixpanel after 5s
    if (token) {
      const load = async () => {
        try {
          const res = await loadMixpanel();
          if (res) {
            setMixpanelInstance(res?.init(token, { debug: false }, "sprint"));
          }
        } catch (error) {}
      };

      setTimeout(() => {
        load();
      }, DURATION_5_SECS_MILLISECONDS);
    }
  }, [token]);

  const handlePageView = useCallback(
    (page: string, options?: any) => {
      if (mixpanelInstance) {
        mixpanelInstance?.track(`${mixpanelEvents.PageView} (${page})`, {
          page,
          ...options,
        });
      }
    },
    [mixpanelInstance],
  );

  const value: MixpanelContext = {
    handlePageView,
    mixpanelInstance,
  };

  return <Context.Provider value={value} {...props} />;
}

function useMixpanel() {
  const context = React.useContext(Context);
  if (isUndefined(context)) {
    throw new Error(`useMixpanel must be used within a MixpanelProvider`);
  }

  return { ...context };
}

export type { MixpanelContext };
export { useMixpanel, MixpanelProvider };
